import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import GeoBanner from "./components/GeoBanner";
import FloatingCTA from "./components/FloatingCTA";
import CookieConsent from "./components/CookieConsent";
import Navbar from "./components/Navbar";
import SiteFooter from "./components/SiteFooter";

const loadIndex = () => import("./pages/Index");
const loadSolutions = () => import("./pages/Solutions");
const loadSolutionDetail = () => import("./pages/SolutionDetail");
const loadCalculator = () => import("./pages/Calculator");
const loadAbout = () => import("./pages/About");
const loadContact = () => import("./pages/Contact");
const loadComingSoon = () => import("./pages/ComingSoon");
const loadLiFi = () => import("./pages/LiFi");
const loadOffice = () => import("./pages/Office");
const loadDataCenter = () => import("./pages/DataCenter");
const loadPerformanceLab = () => import("./pages/PerformanceLab");
const loadManufacturing = () => import("./pages/Manufacturing");
const loadTerms = () => import("./pages/Terms");
const loadPrivacy = () => import("./pages/Privacy");
const loadNotFound = () => import("./pages/NotFound");

// Lazy-load route components to reduce initial JS bundle
const Index = lazy(loadIndex);
const Solutions = lazy(loadSolutions);
const SolutionDetail = lazy(loadSolutionDetail);
const Calculator = lazy(loadCalculator);
const About = lazy(loadAbout);
const Contact = lazy(loadContact);
const ComingSoon = lazy(loadComingSoon);
const LiFi = lazy(loadLiFi);
const Office = lazy(loadOffice);
const DataCenter = lazy(loadDataCenter);
const PerformanceLab = lazy(loadPerformanceLab);
const Manufacturing = lazy(loadManufacturing);
const Terms = lazy(loadTerms);
const Privacy = lazy(loadPrivacy);
const NotFound = lazy(loadNotFound);

// Only preload lightweight routes that don't pull in heavy deps (e.g. Supabase client)
const lightPreloaders = [
  loadIndex,
  loadSolutions,
  loadSolutionDetail,
  loadAbout,
  loadComingSoon,
  loadLiFi,
  loadOffice,
  loadDataCenter,
  loadManufacturing,
  loadTerms,
  loadPrivacy,
  loadNotFound,
];
// Heavy routes (Contact, Calculator, PerformanceLab) import Supabase — defer further
const heavyPreloaders = [loadContact, loadCalculator, loadPerformanceLab];

const queryClient = new QueryClient();

const PageFallback = () => (
  <main className="min-h-screen bg-background pt-24">
    <div className="container mx-auto px-6 py-10">
      <div className="mx-auto max-w-5xl animate-pulse space-y-6">
        <div className="h-3 w-36 rounded-sm bg-secondary" />
        <div className="h-12 max-w-3xl rounded-sm bg-secondary/90" />
        <div className="h-12 max-w-2xl rounded-sm bg-secondary/70" />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="h-40 rounded-sm border border-border bg-card" />
          <div className="h-40 rounded-sm border border-border bg-card" />
        </div>
      </div>
    </div>
  </main>
);

const App = () => {
  useEffect(() => {
    const preloadTimer = window.setTimeout(() => {
      lightPreloaders.forEach((loader) => { void loader(); });
    }, 1200);
    const heavyTimer = window.setTimeout(() => {
      heavyPreloaders.forEach((loader) => { void loader(); });
    }, 8000);

    return () => { window.clearTimeout(preloadTimer); window.clearTimeout(heavyTimer); };
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <GeoBanner />
            <FloatingCTA />
            <CookieConsent>
              <Navbar />
              <main className="bg-background">
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/solutions" element={<Solutions />} />
                    <Route path="/solutions/:slug" element={<SolutionDetail />} />
                    <Route path="/calculator" element={<Calculator />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/lifi" element={<LiFi />} />
                    <Route path="/industries/office" element={<Office />} />
                    <Route path="/industries/data-center" element={<DataCenter />} />
                    <Route path="/industries/manufacturing" element={<Manufacturing />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/performance-lab" element={<PerformanceLab />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/coming-soon" element={<ComingSoon />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <SiteFooter />
              </main>
            </CookieConsent>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
